import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Users, Camera, Heart } from 'lucide-react';
import { showMessage } from '../../../scripts/showMessage.js';
import CardBasic from '../../ui/Card.jsx';

const SECTIONS = [
    { id: 'contacts', label: 'Kontakte', icon: Users },
    { id: 'jobs', label: 'Jobs', icon: Camera },
    { id: 'weddings', label: 'Hochzeiten', icon: Heart }
];

const actionBarStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    background: 'var(--bg-secondary, #f5f7f9)',
    borderBottom: '1px solid var(--border-light, #e2e8f0)'
};

const backButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    border: '1px solid var(--border-medium, #cbd5e1)',
    borderRadius: '8px',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-primary, #2c3e50)',
    fontSize: '0.875rem',
    fontWeight: '500',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
};

const containerStyle = {
    display: 'flex',
    minHeight: '500px'
};

const sidebarStyle = {
    width: '220px',
    flexShrink: 0,
    borderRight: '1px solid var(--border-light, #e2e8f0)',
    background: 'var(--bg-secondary, #f5f7f9)'
};

const sidebarItemStyle = (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    padding: '0.75rem 1.25rem',
    fontSize: '0.875rem',
    fontWeight: isActive ? '600' : '400',
    color: isActive ? 'var(--accent-slate, #5b7c8d)' : 'var(--text-secondary, #64748b)',
    background: isActive ? 'var(--bg-card, #ffffff)' : 'transparent',
    borderLeft: isActive ? '3px solid var(--accent-slate, #5b7c8d)' : '3px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
});

const contentStyle = {
    flex: 1,
    padding: '1.5rem',
    overflowY: 'auto'
};

const mobileTabBarStyle = {
    display: 'flex',
    overflowX: 'auto',
    borderBottom: '1px solid var(--border-light, #e2e8f0)',
    background: 'var(--bg-secondary, #f5f7f9)',
    padding: '0 1rem'
};

const mobileTabStyle = (isActive) => ({
    padding: '0.875rem 1rem',
    fontSize: '0.8125rem',
    fontWeight: '500',
    color: isActive ? 'var(--accent-slate, #5b7c8d)' : 'var(--text-secondary, #64748b)',
    whiteSpace: 'nowrap',
    borderBottom: isActive ? '2px solid var(--accent-slate, #5b7c8d)' : '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
});

const fieldRowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem 2rem',
    marginBottom: '0.5rem'
};

const fieldGroupStyle = {
    marginBottom: '1rem'
};

const fieldLabelStyle = {
    fontSize: '0.75rem',
    fontWeight: '500',
    color: 'var(--text-secondary, #64748b)',
    marginBottom: '0.25rem'
};

const fieldInputStyle = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    border: '1px solid var(--border-medium, #cbd5e1)',
    borderRadius: '6px',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-primary, #2c3e50)',
    transition: 'border-color 0.2s ease',
    outline: 'none'
};

const CustomFieldsSettings = () => {
    const [activeSection, setActiveSection] = useState('contacts');
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [labels, setLabels] = useState({
        contacts: {},
        jobs: {},
        weddings: {}
    });
    const [hasChanges, setHasChanges] = useState({
        contacts: false,
        jobs: false,
        weddings: false
    });

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchLabels();
    }, []);

    const fetchLabels = async () => {
        try {
            const response = await fetch('/index.php/settings/getCustomFieldLabels');
            if (!response.ok) throw new Error('Failed to fetch');
            const result = await response.json();
            if (result.success) {
                setLabels(result.labels);
            } else {
                showMessage(result.error || 'Fehler beim Laden', 'error');
            }
        } catch (error) {
            console.error('Error fetching custom field labels:', error);
            showMessage('Fehler beim Laden der Zusatzfelder', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLabelChange = (entityType, fieldNumber, value) => {
        setLabels(prev => ({
            ...prev,
            [entityType]: {
                ...prev[entityType],
                [fieldNumber]: value
            }
        }));
        setHasChanges(prev => ({
            ...prev,
            [entityType]: true
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const typesToSave = Object.entries(hasChanges)
                .filter(([, changed]) => changed)
                .map(([type]) => type);

            if (typesToSave.length === 0) {
                showMessage('Keine Änderungen vorhanden', 'info');
                setSaving(false);
                return;
            }

            for (const entityType of typesToSave) {
                const response = await fetch('/index.php/settings/saveCustomFieldLabels', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        entity_type: entityType,
                        labels: labels[entityType]
                    })
                });

                const result = await response.json();
                if (!result.success) {
                    throw new Error(result.error || `Fehler beim Speichern von ${entityType}`);
                }
            }

            setHasChanges({ contacts: false, jobs: false, weddings: false });
            showMessage('Zusatzfelder gespeichert', 'success');
        } catch (error) {
            console.error('Error saving custom field labels:', error);
            showMessage(error.message || 'Fehler beim Speichern', 'error');
        } finally {
            setSaving(false);
        }
    };

    const renderFieldGrid = (entityType) => {
        const currentLabels = labels[entityType] || {};
        const leftFields = [1, 2, 3, 4, 5];
        const rightFields = [6, 7, 8, 9, 10];

        return (
            <div>
                <div style={fieldRowStyle}>
                    <div>
                        {leftFields.map(num => (
                            <div key={num} style={fieldGroupStyle}>
                                <div style={fieldLabelStyle}>Zusatzfeld {num}</div>
                                <input
                                    type="text"
                                    style={fieldInputStyle}
                                    placeholder={`Label für Zusatzfeld ${num}`}
                                    value={currentLabels[num] || ''}
                                    onChange={(e) => handleLabelChange(entityType, num, e.target.value)}
                                    onFocus={(e) => e.target.style.borderColor = 'var(--accent-slate, #5b7c8d)'}
                                    onBlur={(e) => e.target.style.borderColor = 'var(--border-medium, #cbd5e1)'}
                                />
                            </div>
                        ))}
                    </div>
                    <div>
                        {rightFields.map(num => (
                            <div key={num} style={fieldGroupStyle}>
                                <div style={fieldLabelStyle}>Zusatzfeld {num}</div>
                                <input
                                    type="text"
                                    style={fieldInputStyle}
                                    placeholder={`Label für Zusatzfeld ${num}`}
                                    value={currentLabels[num] || ''}
                                    onChange={(e) => handleLabelChange(entityType, num, e.target.value)}
                                    onFocus={(e) => e.target.style.borderColor = 'var(--accent-slate, #5b7c8d)'}
                                    onBlur={(e) => e.target.style.borderColor = 'var(--border-medium, #cbd5e1)'}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderSection = () => {
        const section = SECTIONS.find(s => s.id === activeSection);
        if (!section) return null;

        const Icon = section.icon;

        return (
            <div>
                <div className="d-flex align-items-center gap-2 mb-3">
                    <Icon size={20} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                    <h5 className="mb-0" style={{ color: 'var(--text-primary, #2c3e50)', fontWeight: '600' }}>
                        {section.label}
                    </h5>
                </div>
                <p className="mb-4" style={{ fontSize: '0.875rem', color: 'var(--text-secondary, #64748b)' }}>
                    Definiere die Labels für bis zu 10 Zusatzfelder. Nur Felder mit einem Label werden bei {section.label} angezeigt.
                </p>
                {renderFieldGrid(activeSection)}
            </div>
        );
    };

    if (isLoading) {
        return (
            <CardBasic>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Laden...</span>
                    </div>
                </div>
            </CardBasic>
        );
    }

    const anyChanges = Object.values(hasChanges).some(v => v);

    return (
        <CardBasic style={{ padding: 0, overflow: 'hidden' }}>
            {/* Action Bar */}
            <div style={actionBarStyle}>
                <a href="index.php/settings" style={backButtonStyle}>
                    <ArrowLeft size={18} />
                    Zurück
                </a>
                <button
                    className={`btn ${anyChanges ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={handleSave}
                    disabled={saving || !anyChanges}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                    {saving ? (
                        <span className="spinner-border spinner-border-sm" role="status" />
                    ) : (
                        <Save size={16} />
                    )}
                    Speichern
                </button>
            </div>

            {/* Mobile Tab Bar */}
            {isMobile && (
                <div style={mobileTabBarStyle}>
                    {SECTIONS.map(section => {
                        const Icon = section.icon;
                        return (
                            <div
                                key={section.id}
                                style={mobileTabStyle(activeSection === section.id)}
                                onClick={() => setActiveSection(section.id)}
                            >
                                <span className="d-flex align-items-center gap-1">
                                    <Icon size={14} />
                                    {section.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Main Content */}
            <div style={isMobile ? { padding: '1.5rem' } : containerStyle}>
                {/* Desktop Sidebar */}
                {!isMobile && (
                    <div style={sidebarStyle}>
                        <div style={{ padding: '0.5rem 0' }}>
                            {SECTIONS.map(section => {
                                const Icon = section.icon;
                                const isActive = activeSection === section.id;
                                return (
                                    <div
                                        key={section.id}
                                        style={sidebarItemStyle(isActive)}
                                        onClick={() => setActiveSection(section.id)}
                                    >
                                        <Icon size={16} />
                                        <span>{section.label}</span>
                                        {hasChanges[section.id] && (
                                            <span style={{
                                                width: '6px',
                                                height: '6px',
                                                borderRadius: '50%',
                                                background: 'var(--accent-mint, #88b7a0)',
                                                marginLeft: 'auto'
                                            }} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Content Area */}
                <div style={isMobile ? {} : contentStyle}>
                    {renderSection()}
                </div>
            </div>
        </CardBasic>
    );
};

export default CustomFieldsSettings;
